import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useExchangeStore } from './stores/exchangeStore';
import { usePortfolioStore } from './stores/portfolioStore';
import { fetchBalanceWithCredentials, getConnectionEntities, getConnectionCredentials, deleteConnectionEntity, storeApiKey } from './api/connections';
import { getExchangeInfos } from './api/exchanges';
import { getRequiredCredentialsForExchange } from './exchange/getRequiredCredentials';
import { createSyncTimestamp, formatSyncDate } from './utils/syncDate';

const DEFAULT_MANAGED_REQUIRED_CREDENTIALS = ['apiKey', 'secret'];

const formatSensitiveKey = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const normalized = value.trim();
  if (!normalized) {
    return '';
  }

  if (normalized.length <= 12) {
    return normalized;
  }

  return `${normalized.slice(0, 6)}...${normalized.slice(-3)}`;
};

const isLikelyNetworkIssue = (message) => /failed to fetch|network|err_network|err_connection|timeout|timed out|502|503|504/i.test(String(message || ''));

const exchangeActionButtonSx = {
  borderRadius: 999,
  px: 1.6,
  py: 0.9,
  fontWeight: 800,
  textTransform: 'none',
  minWidth: 0,
};


const ConnectedExchangesSection = ({ userId, syncAllRequestId = 0, onSyncAllComplete }) => {
  const [entities, setEntities] = useState([]); // connection_entity list
  const [exchangeInfos, setExchangeInfos] = useState({});
  const [loading, setLoading] = useState({});
  const [errorMsg, setErrorMsg] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [lastSync, setLastSync] = useState({});
  const [expandedAssetsByEntityId, setExpandedAssetsByEntityId] = useState({});
  const [managedEntityId, setManagedEntityId] = useState(null);
  const [manageForm, setManageForm] = useState({ apiKey: '', apiSecret: '', passphrase: '', password: '' });
  const [manageSaving, setManageSaving] = useState(false);
  const [manageTesting, setManageTesting] = useState(false);
  const [manageNotice, setManageNotice] = useState('');
  const [manageError, setManageError] = useState('');
  const setSyncing = useExchangeStore((s) => s.setSyncing);
  const setConnected = useExchangeStore((s) => s.setConnected);
  const setError = useExchangeStore((s) => s.setError);
  const setDisconnected = useExchangeStore((s) => s.setDisconnected);
  const exchanges = useExchangeStore((s) => s.exchanges);
  const applySyncResult = usePortfolioStore((s) => s.applySyncResult);
  const positions = usePortfolioStore((s) => s.exchangesPositions) || [];

  // Charge les exchanges et credentials depuis le backend
  const fetchEntitiesAndCredentials = React.useCallback(async () => {
      try {
        const [entitiesList, credentialsList] = await Promise.all([
          getConnectionEntities(),
          getConnectionCredentials()
        ]);
        const exchangeCodes = entitiesList
          .filter(e => e.type === 'EXCHANGE' && (!userId || e.userId === userId))
          .map(e => e.name)
          .filter(Boolean);
        const exchangeInfoList = exchangeCodes.length > 0 ? await getExchangeInfos(exchangeCodes) : [];
        const exchangeInfoByCode = {};
        exchangeInfoList.forEach(info => {
          if (info) {
            // Index by requestedCode (CCXT ID) so aliases like gateio→GATE, mexc→MXC resolve correctly
            const key = info.requestedCode || info.code;
            if (key) exchangeInfoByCode[String(key).toUpperCase()] = info;
          }
        });
        // Filtrer uniquement les exchanges
        const filteredEntities = entitiesList.filter(e => e.type === 'EXCHANGE' && (!userId || e.userId === userId));
        setEntities(filteredEntities);
        // Stocker les credentials par entityId
        const credsByEntity = {};
        credentialsList.forEach(c => {
          if (c.connectionEntity && c.connectionEntity.id) {
            const entityId = c.connectionEntity.id;
            const entityName = c.connectionEntity.name;
            credsByEntity[entityId] = {
              ...c,
              image: entityName ? exchangeInfoByCode[String(entityName).toUpperCase()]?.image : undefined
            };
          }
        });
        setExchangeInfos(credsByEntity);
        // Synchronise le store Zustand exchanges avec la vraie liste
        const entityIds = new Set(filteredEntities.map(e => String(e.id)));
        // Supprime les exchanges non présents dans la base
        const exchanges = useExchangeStore.getState().exchanges;
        Object.keys(exchanges).forEach(id => {
          if (!entityIds.has(String(id))) {
            useExchangeStore.getState().removeExchange(id);
          }
        });
      } catch (e) {
        setEntities([]);
        setExchangeInfos({});
      }
  }, [userId]);

  React.useEffect(() => {
    void fetchEntitiesAndCredentials();
  }, [fetchEntitiesAndCredentials]);

  // Nouvelle logique de synchronisation utilisant la nouvelle structure backend
  const handleSync = React.useCallback(async (entityId) => {
    setSyncing(entityId);
    setLoading(l => ({ ...l, [entityId]: true }));
    setErrorMsg(em => ({ ...em, [entityId]: '' }));
    setGlobalError('');
    try {
      // Récupérer les credentials liés à l'entity
      const creds = exchangeInfos[entityId];
      if (!creds || !creds.apiKeyEncrypted || !creds.apiSecretEncrypted) {
        setError(entityId);
        setErrorMsg(em => ({ ...em, [entityId]: 'Aucune clé API trouvée pour cette connexion.' }));
        setLoading(l => ({ ...l, [entityId]: false }));
        return;
      }
      const entity = entities.find(e => e.id === entityId);
      const result = await fetchBalanceWithCredentials({
        userId: entity ? entity.userId : undefined,
        exchange: entity ? entity.name : undefined
      });
      if (result && result.balances) {
        // Convert to PortfolioPosition[]
        const portfolio = result.balances.map(b => ({
          assetId: b.asset,
          quantity: b.total,
          sourceType: 'EXCHANGE',
          sourceId: entityId
        }));
        applySyncResult(portfolio);
        // Mark as CONNECTED in Zustand using backend entity id for global status
        setConnected(String(entityId));
        // Enregistre la date de synchronisation
        const syncTime = createSyncTimestamp();
        setLastSync(ls => ({ ...ls, [entityId]: syncTime }));
        localStorage.setItem('ecotoneai_lastSync_' + entityId, syncTime);
        // Dispatch custom event to trigger entity refresh in GlobalConnectionStatusHero
        window.dispatchEvent(new Event('refresh-connection-entities'));
        window.dispatchEvent(new Event('refresh-connection-sync-state'));
      } else if (result && result.error) {
        setError(entityId);
        setErrorMsg(em => ({ ...em, [entityId]: result.error }));
      } else {
        setError(entityId);
        setErrorMsg(em => ({ ...em, [entityId]: 'Erreur inconnue lors de la synchronisation.' }));
      }
    } catch (err) {
      setError(entityId);
      let msg = '';
      // Try to extract a specific error message from backend/microservice
      if (err && err.message) {
        try {
          // Try to parse as JSON if possible
          const parsed = JSON.parse(err.message);
          if (parsed && parsed.error) {
            msg = parsed.error;
          } else if (parsed && parsed.message) {
            msg = parsed.message;
          } else {
            msg = err.message;
          }
        } catch (e) {
          // Not JSON, use as plain text
          msg = err.message;
        }
      } else {
        msg = 'Erreur réseau ou serveur.';
      }
      if (isLikelyNetworkIssue(err?.message)) {
        msg += ' Si un VPN est actif, désactivez-le temporairement puis réessayez.';
      }
      setErrorMsg(em => ({ ...em, [entityId]: msg }));
      // Si toutes les sync échouent, affiche une erreur globale
      const allFailed = entities.every(e => e.id === entityId || exchanges[e.id] === 'ERROR');
      if (allFailed) setGlobalError('Synchronisation échouée pour tous les exchanges.');
    } finally {
      setLoading(l => ({ ...l, [entityId]: false }));
    }
  }, [applySyncResult, entities, exchangeInfos, exchanges, setConnected, setError, setSyncing]);

  const handleSyncAll = React.useCallback(async () => {
    for (const entity of entities) {
      await handleSync(entity.id);
    }
  }, [entities, handleSync]);

  const toggleAssetsList = React.useCallback((entityId) => {
    setExpandedAssetsByEntityId((current) => ({
      ...current,
      [entityId]: !current[entityId],
    }));
  }, []);

  const handleOpenManage = React.useCallback((entityId) => {
    setManagedEntityId(entityId);
    setManageForm({ apiKey: '', apiSecret: '', passphrase: '', password: '' });
    setManageNotice('');
    setManageError('');
  }, []);

  const handleCloseManage = React.useCallback(() => {
    setManagedEntityId(null);
    setManageSaving(false);
    setManageTesting(false);
  }, []);

  const managedEntity = entities.find((entry) => entry.id === managedEntityId) || null;
  const managedRequiredCredentials = managedEntity?.name
    ? getRequiredCredentialsForExchange(managedEntity.name)
    : DEFAULT_MANAGED_REQUIRED_CREDENTIALS;
  const requiresCredential = React.useCallback(
    (key) => managedRequiredCredentials.includes(key),
    [managedRequiredCredentials]
  );

  const handleManageFieldChange = React.useCallback((field, value) => {
    setManageForm((current) => ({ ...current, [field]: value }));
  }, []);

  const handleSaveManagedCredentials = React.useCallback(async () => {
    if (!managedEntity) {
      return;
    }

    setManageError('');
    setManageNotice('');

    if (requiresCredential('apiKey') && !manageForm.apiKey.trim()) {
      setManageError('La clé API est obligatoire pour cet exchange.');
      return;
    }
    if (requiresCredential('secret') && !manageForm.apiSecret.trim()) {
      setManageError('Le secret API est obligatoire pour cet exchange.');
      return;
    }
    if (requiresCredential('passphrase') && !manageForm.passphrase.trim()) {
      setManageError('La passphrase est obligatoire pour cet exchange.');
      return;
    }
    if (requiresCredential('password') && !manageForm.password.trim()) {
      setManageError('Le mot de passe API est obligatoire pour cet exchange.');
      return;
    }

    setManageSaving(true);
    try {
      await storeApiKey({
        userId: managedEntity.userId,
        exchange: managedEntity.name,
        apiKey: manageForm.apiKey.trim(),
        apiSecret: manageForm.apiSecret.trim(),
        passphrase: manageForm.passphrase.trim() || undefined,
        password: manageForm.password.trim() || undefined,
      });
      setManageNotice('Clés API enregistrées avec succès.');
      await fetchEntitiesAndCredentials();
      window.dispatchEvent(new Event('refresh-connection-entities'));
    } catch (error) {
      setManageError(error?.message || 'Impossible d’enregistrer les clés API.');
    } finally {
      setManageSaving(false);
    }
  }, [fetchEntitiesAndCredentials, manageForm.apiKey, manageForm.apiSecret, manageForm.passphrase, manageForm.password, managedEntity, requiresCredential]);

  const handleTestManagedConnection = React.useCallback(async () => {
    if (!managedEntity) {
      return;
    }
    setManageError('');
    setManageNotice('');
    setManageTesting(true);
    try {
      const result = await fetchBalanceWithCredentials({
        userId: managedEntity.userId,
        exchange: managedEntity.name,
      });
      const balancesCount = Array.isArray(result?.balances) ? result.balances.length : 0;
      setManageNotice(`Connexion valide. ${balancesCount} actifs remontés.`);
    } catch (error) {
      let message = error?.message || 'Test de connexion échoué.';
      if (isLikelyNetworkIssue(error?.message)) {
        message += ' Si un VPN est actif, désactivez-le temporairement puis réessayez.';
      }
      setManageError(message);
    } finally {
      setManageTesting(false);
    }
  }, [managedEntity]);

  const lastHandledSyncAllRequestRef = React.useRef(0);
  React.useEffect(() => {
    if (!syncAllRequestId || syncAllRequestId === lastHandledSyncAllRequestRef.current) {
      return;
    }

    lastHandledSyncAllRequestRef.current = syncAllRequestId;
    let cancelled = false;

    const run = async () => {
      try {
        await handleSyncAll();
      } finally {
        if (!cancelled && onSyncAllComplete) {
          onSyncAllComplete('exchanges', syncAllRequestId);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [handleSyncAll, onSyncAllComplete, syncAllRequestId]);

  const handleRemove = async (entityId) => {
    setLoading(l => ({ ...l, [entityId]: true }));
    try {
      await deleteConnectionEntity(entityId);
      setEntities(entities.filter(e => e.id !== entityId));
      setDisconnected(entityId);
      applySyncResult([]);
      // Dispatch custom event to trigger entity refresh in GlobalConnectionStatusHero
      window.dispatchEvent(new Event('refresh-connection-entities'));
    } catch (err) {
      setError(entityId);
    } finally {
      setLoading(l => ({ ...l, [entityId]: false }));
    }
  };

  // Charge la date de synchronisation au montage
  React.useEffect(() => {
    const syncs = {};
    entities.forEach((entity) => {
      const id = entity?.id;
      if (id == null) {
        return;
      }
      const val = localStorage.getItem('ecotoneai_lastSync_' + id);
      if (val) {
        syncs[id] = val;
      }
    });
    setLastSync(syncs);
  }, [entities]);

  return (
    <div className="connected-exchanges-section">
      <div className="section-title">Exchanges connectés</div>
      {entities.length === 0 && <div>Aucun exchange connecté</div>}
      {globalError && (
        <div className="global-error" style={{ color: '#c62828', fontWeight: 600, marginBottom: 12 }}>{globalError}</div>
      )}
      {/* Affichage du compteur réel */}
      <div style={{ color: '#1976d2', fontWeight: 500, marginBottom: 8 }}>
        {entities.length} connexion{entities.length > 1 ? 's' : ''} affichée{entities.length > 1 ? 's' : ''}
      </div>
      {entities.map((entity) => {
        const id = entity.id;
        const name = entity.name;
        const assets = positions.filter(p => p.sourceId === id);
        const basicAssets = Array.from(
          new Set(
            assets
              .map((asset) => String(asset?.assetId || '').trim().toUpperCase())
              .filter(Boolean)
          )
        );
        const isAssetsListExpanded = Boolean(expandedAssetsByEntityId[id]);
        const logoUrl = exchangeInfos[id]?.image || '/logo192.png';
        const apiKeyPreview = formatSensitiveKey(exchangeInfos[id]?.apiKeyEncrypted || exchangeInfos[id]?.apiKey || exchangeInfos[name]?.apiKeyEncrypted || exchangeInfos[name]?.apiKey);
        return (
          <div className="exchange-card" key={id}>
            <div className="exchange-logo">
              <img src={logoUrl} alt={name + ' logo'} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }} />
            </div>
            <div className="exchange-summary">
              <div className="exchange-name">{name}</div>
              {apiKeyPreview ? (
                <div className="exchange-api-key">Clé API: {apiKeyPreview}</div>
              ) : null}
              <div className="exchange-status">Synchronisé</div>
            </div>
            <div className="exchange-meta">
              <button
                type="button"
                className="exchange-assets"
                onClick={() => toggleAssetsList(id)}
                disabled={basicAssets.length === 0}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  margin: 0,
                  color: basicAssets.length > 0 ? '#214f77' : '#6b7b82',
                  cursor: basicAssets.length > 0 ? 'pointer' : 'default',
                  fontWeight: 700,
                  textAlign: 'left',
                }}
                title={basicAssets.length > 0 ? 'Afficher/masquer la liste des actifs détectés.' : 'Aucun actif détecté'}
              >
                {assets.length} actifs détectés{basicAssets.length > 0 ? (isAssetsListExpanded ? ' ▲' : ' ▼') : ''}
              </button>
              {isAssetsListExpanded && basicAssets.length > 0 ? (
                <div style={{ marginTop: 4, fontSize: 12, color: '#556970' }}>
                  {basicAssets.join(', ')}
                </div>
              ) : null}
              <div className="exchange-sync-time">
                Dernière synchronisation : {formatSyncDate(lastSync[id])}
              </div>
              {errorMsg[id] && (
                <div className="exchange-error">{errorMsg[id]}</div>
              )}
            </div>
            <div className="exchange-actions">
              <Button
                type="button"
                onClick={() => handleSync(id)}
                disabled={loading[id]}
                title="Synchronise les données de l’exchange."
                variant="contained"
                startIcon={loading[id] ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AutorenewRoundedIcon />}
                sx={{
                  ...exchangeActionButtonSx,
                  background: 'linear-gradient(135deg, #17323c 0%, #2d5565 100%)',
                  boxShadow: '0 14px 28px rgba(23, 50, 60, 0.16)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #12262d 0%, #254755 100%)',
                  },
                }}
              >
                {loading[id] ? 'Synchronisation...' : 'Actualiser'}
              </Button>
              <Button
                type="button"
                onClick={() => handleRemove(id)}
                disabled={loading[id]}
                title="Supprime la connexion et toutes les données associées."
                variant="outlined"
                startIcon={<DeleteOutlineRoundedIcon />}
                sx={{
                  ...exchangeActionButtonSx,
                  color: '#aa3d33',
                  borderColor: 'rgba(170, 61, 51, 0.22)',
                  backgroundColor: 'rgba(170, 61, 51, 0.06)',
                  '&:hover': {
                    borderColor: 'rgba(170, 61, 51, 0.34)',
                    backgroundColor: 'rgba(170, 61, 51, 0.10)',
                  },
                }}
              >
                Supprimer
              </Button>
              <Button
                type="button"
                onClick={() => handleOpenManage(id)}
                disabled={loading[id]}
                title="Gérer les paramètres de l’exchange"
                variant="outlined"
                startIcon={<EditRoundedIcon />}
                sx={{
                  ...exchangeActionButtonSx,
                  color: '#17323c',
                  borderColor: 'rgba(23, 50, 60, 0.18)',
                  backgroundColor: 'rgba(255,255,255,0.74)',
                  '&:hover': {
                    borderColor: 'rgba(23, 50, 60, 0.3)',
                    backgroundColor: 'rgba(255,255,255,0.94)',
                  },
                }}
              >
                Gérer
              </Button>
            </div>
          </div>
        );
      })}

      <Dialog
        open={managedEntityId != null}
        onClose={handleCloseManage}
        fullWidth
        maxWidth="sm"
      >
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
              Gérer la connexion exchange
            </Typography>
            <IconButton onClick={handleCloseManage} aria-label="Fermer">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>

          {managedEntity ? (
            <>
            {(() => {
            const managedAssets = positions.filter((p) => p.sourceId === managedEntity.id);
            const managedBasicAssets = Array.from(
              new Set(
                managedAssets
                  .map((asset) => String(asset?.assetId || '').trim().toUpperCase())
                  .filter(Boolean)
              )
            );
            const managedApiKeyPreview = formatSensitiveKey(
              exchangeInfos[managedEntity.id]?.apiKeyEncrypted
              || exchangeInfos[managedEntity.id]?.apiKey
              || exchangeInfos[managedEntity.name]?.apiKeyEncrypted
              || exchangeInfos[managedEntity.name]?.apiKey
            );

            return (
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                <Box sx={{ borderRadius: 2, border: '1px solid rgba(23,50,60,0.12)', background: 'rgba(255,255,255,0.78)', p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>
                    {managedEntity.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#556970', mt: 0.75 }}>
                    Clé API: {managedApiKeyPreview || 'Non disponible'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#556970', mt: 0.5 }}>
                    Dernière synchronisation: {formatSyncDate(lastSync[managedEntity.id])}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#556970', mt: 0.5 }}>
                    Actifs détectés: {managedBasicAssets.length > 0 ? managedBasicAssets.join(', ') : 'Aucun'}
                  </Typography>
                </Box>

                <Box sx={{ borderRadius: 2, border: '1px solid rgba(23,50,60,0.12)', background: 'rgba(255,255,255,0.78)', p: 1.5 }}>
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>
                      Rotation / édition des clés API
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7b82' }}>
                      Renseignez uniquement les champs requis pour {managedEntity.name.toUpperCase()} : {managedRequiredCredentials.join(', ')}
                    </Typography>

                    {requiresCredential('apiKey') ? (
                      <TextField
                        size="small"
                        label="Clé API"
                        value={manageForm.apiKey}
                        onChange={(e) => handleManageFieldChange('apiKey', e.target.value)}
                      />
                    ) : null}
                    {requiresCredential('secret') ? (
                      <TextField
                        size="small"
                        label="Secret API"
                        value={manageForm.apiSecret}
                        onChange={(e) => handleManageFieldChange('apiSecret', e.target.value)}
                      />
                    ) : null}
                    {requiresCredential('passphrase') ? (
                      <TextField
                        size="small"
                        label="Passphrase"
                        value={manageForm.passphrase}
                        onChange={(e) => handleManageFieldChange('passphrase', e.target.value)}
                      />
                    ) : null}
                    {requiresCredential('password') ? (
                      <TextField
                        size="small"
                        label="Mot de passe API"
                        value={manageForm.password}
                        onChange={(e) => handleManageFieldChange('password', e.target.value)}
                      />
                    ) : null}

                    {manageNotice ? <Alert severity="success">{manageNotice}</Alert> : null}
                    {manageError ? <Alert severity="error">{manageError}</Alert> : null}
                  </Stack>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  <Button
                    type="button"
                    onClick={() => {
                      void handleSaveManagedCredentials();
                    }}
                    disabled={manageSaving || manageTesting || loading[managedEntity.id]}
                    variant="outlined"
                    startIcon={manageSaving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <EditRoundedIcon />}
                    sx={{
                      ...exchangeActionButtonSx,
                      color: '#17323c',
                      borderColor: 'rgba(23, 50, 60, 0.18)',
                      backgroundColor: 'rgba(255,255,255,0.74)',
                      '&:hover': {
                        borderColor: 'rgba(23, 50, 60, 0.3)',
                        backgroundColor: 'rgba(255,255,255,0.94)',
                      },
                    }}
                  >
                    {manageSaving ? 'Enregistrement...' : 'Enregistrer les clés'}
                  </Button>

                  <Button
                    type="button"
                    onClick={() => {
                      void handleTestManagedConnection();
                    }}
                    disabled={manageTesting || manageSaving || loading[managedEntity.id]}
                    variant="outlined"
                    startIcon={manageTesting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AutorenewRoundedIcon />}
                    sx={{
                      ...exchangeActionButtonSx,
                      color: '#17323c',
                      borderColor: 'rgba(23, 50, 60, 0.18)',
                      backgroundColor: 'rgba(255,255,255,0.74)',
                      '&:hover': {
                        borderColor: 'rgba(23, 50, 60, 0.3)',
                        backgroundColor: 'rgba(255,255,255,0.94)',
                      },
                    }}
                  >
                    {manageTesting ? 'Test en cours...' : 'Tester la connexion'}
                  </Button>

                  <Button
                    type="button"
                    onClick={() => {
                      void handleSync(managedEntity.id);
                    }}
                    disabled={loading[managedEntity.id]}
                    variant="contained"
                    startIcon={loading[managedEntity.id] ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AutorenewRoundedIcon />}
                    sx={{
                      ...exchangeActionButtonSx,
                      background: 'linear-gradient(135deg, #17323c 0%, #2d5565 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #12262d 0%, #254755 100%)',
                      },
                    }}
                  >
                    {loading[managedEntity.id] ? 'Synchronisation...' : 'Actualiser'}
                  </Button>

                  <Button
                    type="button"
                    onClick={async () => {
                      await handleRemove(managedEntity.id);
                      handleCloseManage();
                    }}
                    disabled={loading[managedEntity.id]}
                    variant="outlined"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    sx={{
                      ...exchangeActionButtonSx,
                      color: '#aa3d33',
                      borderColor: 'rgba(170, 61, 51, 0.22)',
                      backgroundColor: 'rgba(170, 61, 51, 0.06)',
                      '&:hover': {
                        borderColor: 'rgba(170, 61, 51, 0.34)',
                        backgroundColor: 'rgba(170, 61, 51, 0.10)',
                      },
                    }}
                  >
                    Supprimer
                  </Button>
                </Stack>
              </Stack>
            );
          })()}
            </>
          ) : (
            <Typography variant="body2" sx={{ mt: 2, color: '#6b7b82' }}>
              Connexion introuvable.
            </Typography>
          )}
        </Box>
      </Dialog>
    </div>
  );
};

export default ConnectedExchangesSection;
